import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { AlertCircle, X } from "lucide-react";
import Combobox from "@/components/form/Combobox";
import { useWorkflowReferenceOptions } from "@/hooks/api/queries/workflows.queries";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/utils";
import type {
  WorkflowReferenceAction,
  WorkflowReferenceItem,
  WorkflowReferenceKind,
} from "@/types/workflow-reference.types";
import {
  useResolvedWorkflowReferences,
  workflowReferenceKey,
} from "./reference-resolution-provider";
import {
  FALLBACK_REFERENCE_ICON,
  ICON_BY_KIND,
  SEARCH_KEY_BY_KIND,
  UNAVAILABLE_KEY_BY_KIND,
} from "./reference-picker-config";

export interface ReferencePickerProps {
  kind: WorkflowReferenceKind;
  value: string[];
  onChange: (ids: string[]) => void;
  mode?: "single" | "multi";
  action?: WorkflowReferenceAction;
  nodeType?: string;
  fieldKey?: string;
  disabled?: boolean;
  placeholder?: string;
  optionFilter?: (item: WorkflowReferenceItem) => boolean;
}

function labelForItem(
  item: WorkflowReferenceItem | undefined,
  kind: WorkflowReferenceKind,
  t: ReturnType<typeof useTranslations>,
) {
  if (item?.state === "available" && item.label) return item.label;
  const key = UNAVAILABLE_KEY_BY_KIND[kind] ?? "drawer.reference.unavailable";
  return t(key);
}

function referenceOptionDepth(item: WorkflowReferenceItem) {
  if (item.kind !== "folder") return 0;
  const depth = item.meta?.depth;
  return typeof depth === "number" && Number.isFinite(depth) ? Math.max(0, depth) : 0;
}

export function ReferencePicker({
  kind,
  value,
  onChange,
  mode = "multi",
  action = "read",
  nodeType,
  fieldKey,
  disabled,
  placeholder,
  optionFilter,
}: ReferencePickerProps) {
  const t = useTranslations("workflows");
  const [q, setQ] = useState("");
  const [knownItems, setKnownItems] = useState<Record<string, WorkflowReferenceItem>>({});
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const searchSettling = q.trim() !== debouncedQ;

  const optionsQuery = useWorkflowReferenceOptions(
    {
      kind,
      action,
      nodeType,
      fieldKey,
      q: debouncedQ || undefined,
      limit: 30,
    },
    { enabled: !disabled },
  );
  const resolved = useResolvedWorkflowReferences({ kind, action, nodeType, fieldKey, ids: value });

  useEffect(() => {
    const rows = optionsQuery.data?.data.items ?? [];
    if (rows.length === 0) return;
    setKnownItems((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of rows) {
        const existing = prev[item.id];
        if (
          existing?.label === item.label &&
          existing?.subtitle === item.subtitle &&
          existing?.state === item.state
        ) {
          continue;
        }
        next[item.id] = item;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [optionsQuery.data]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const rawOptions = searchSettling || optionsQuery.isFetching ? [] : optionsQuery.data?.data.items ?? [];
  const options = rawOptions
    .filter((item) => !selectedSet.has(item.id))
    .filter((item) => optionFilter?.(item) ?? true)
    .map((item) => ({
      value: item.id,
      searchText: `${item.label ?? ""} ${item.subtitle ?? ""}`,
      data: item,
    }));

  const selectedItems = value.map((id) => {
    const item = resolved.itemsByKey[workflowReferenceKey(kind, id, action)] ?? knownItems[id];
    return { id, item, label: labelForItem(item, kind, t) };
  });

  const Icon = ICON_BY_KIND[kind] ?? FALLBACK_REFERENCE_ICON;
  const isLoading = optionsQuery.isLoading || optionsQuery.isFetching || searchSettling;
  const searchPlaceholder = placeholder ?? t(SEARCH_KEY_BY_KIND[kind] ?? "drawer.reference.search");

  const selectItem = (option: { value: string; data: WorkflowReferenceItem }) => {
    if (!selectedSet.has(option.value)) {
      setKnownItems((prev) => ({ ...prev, [option.value]: option.data }));
      onChange(mode === "single" ? [option.value] : [...value, option.value]);
    }
    setQ("");
  };

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        value={q}
        onChange={setQ}
        options={options}
        onSelect={selectItem}
        placeholder={searchPlaceholder}
        disabled={disabled}
        ariaLabel={searchPlaceholder}
        headerSlot={
          optionsQuery.isError ? (
            <div className="px-3 py-2 text-xs text-[var(--destructive)]">
              {t("drawer.reference.load_failed")}
            </div>
          ) : isLoading ? (
            <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
              {t("drawer.reference.loading")}
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
              {t("drawer.reference.no_results")}
            </div>
          ) : null
        }
        renderOption={({ option }) => (
          <div
            className="flex min-w-0 items-center gap-2"
            style={{ paddingLeft: referenceOptionDepth(option.data) * 16 }}
          >
            <Icon className="size-3.5 shrink-0 text-[var(--muted-foreground)]" />
            <span className="min-w-0 flex-1 truncate text-sm">{option.data.label}</span>
            {option.data.subtitle && (
              <span className="shrink-0 text-[10px] uppercase text-[var(--muted-foreground)]">
                {option.data.subtitle}
              </span>
            )}
          </div>
        )}
      />
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-busy={resolved.isLoading}>
          {selectedItems.map(({ id, item, label }) => {
            const unavailable = item?.state === "unavailable";
            const ChipIcon = unavailable ? AlertCircle : Icon;
            return (
              <span
                key={id}
                className={cn(
                  "inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                  unavailable
                    ? "bg-[var(--destructive-bg)] text-[var(--destructive)]"
                    : "bg-[var(--muted)] text-[var(--foreground)]",
                )}
              >
                <ChipIcon className="size-3 shrink-0 text-current" />
                <span className="max-w-[180px] truncate">{label}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onChange(value.filter((current) => current !== id))}
                    className="ml-0.5 text-current opacity-70 hover:opacity-100"
                    aria-label={t("drawer.reference.remove", { label })}
                  >
                    <X className="size-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ReferencePicker;
